(() => {
  const sectionSelector = '[id$="__google_reviews_carousel"]';
  const controlsClass = 'okg-reviews-controls';
  const progressClass = 'okg-reviews-progress';
  const progressBarClass = 'okg-reviews-progress__bar';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getTranslateX = (element) => {
    const transform = window.getComputedStyle(element).transform;

    if (!transform || transform === 'none') return 0;

    const matrix = transform.match(/^matrix\((.+)\)$/);
    if (matrix) {
      const values = matrix[1].split(',').map((value) => Number.parseFloat(value.trim()));
      return Number.isFinite(values[4]) ? values[4] : 0;
    }

    const matrix3d = transform.match(/^matrix3d\((.+)\)$/);
    if (matrix3d) {
      const values = matrix3d[1].split(',').map((value) => Number.parseFloat(value.trim()));
      return Number.isFinite(values[12]) ? values[12] : 0;
    }

    return 0;
  };

  const getSlideStep = (slides) => {
    if (slides.length < 2) return slides[0]?.getBoundingClientRect().width || 1;

    const offsets = slides
      .map((slide) => slide.offsetLeft)
      .sort((a, b) => a - b);
    const gaps = offsets
      .slice(1)
      .map((offset, index) => offset - offsets[index])
      .filter((gap) => gap > 1);

    return gaps[0] || slides[0].getBoundingClientRect().width || 1;
  };

  const getMetrics = (section) => {
    const viewport = section.querySelector('.swiper-horizontal');
    const wrapper = viewport?.firstElementChild;
    const progress = section.querySelector(`.${progressClass}`);

    if (!viewport || !wrapper || !progress) return null;

    const slides = [...wrapper.children].filter((slide) => slide.getBoundingClientRect().width > 0);
    if (!slides.length) return null;

    const step = getSlideStep(slides);
    const visibleSlides = Math.max(1, Math.round(viewport.clientWidth / step));
    const maxIndex = Math.max(0, slides.length - visibleSlides);
    const activeIndex = clamp(Math.round(Math.abs(getTranslateX(wrapper)) / step), 0, maxIndex);
    const thumbWidth = clamp((visibleSlides / slides.length) * 100, 18, 70);

    return {
      activeIndex,
      maxIndex,
      progress,
      step,
      thumbWidth,
      viewport,
      wrapper,
    };
  };

  const updateProgress = (section) => {
    const metrics = getMetrics(section);
    if (!metrics) return;

    const { activeIndex, maxIndex, progress, thumbWidth } = metrics;
    const thumbLeft = maxIndex > 0 ? (activeIndex / maxIndex) * (100 - thumbWidth) : 0;
    const prevButton = section.querySelector('.okg-reviews-button--prev');
    const nextButton = section.querySelector('.okg-reviews-button--next');

    progress.style.setProperty('--okg-reviews-progress-width', `${thumbWidth}%`);
    progress.style.setProperty('--okg-reviews-progress-left', `${thumbLeft}%`);
    progress.setAttribute('aria-valuemax', String(maxIndex));
    progress.setAttribute('aria-valuenow', String(activeIndex));

    prevButton?.toggleAttribute('disabled', activeIndex <= 0);
    nextButton?.toggleAttribute('disabled', activeIndex >= maxIndex);
  };

  const setIndex = (section, index, animate = true) => {
    const metrics = getMetrics(section);
    if (!metrics) return;

    const { maxIndex, step, wrapper } = metrics;
    const nextIndex = clamp(Math.round(index), 0, maxIndex);

    section.dataset.okgReviewsIndex = String(nextIndex);
    wrapper.style.transitionDuration = animate ? '300ms' : '0ms';
    wrapper.style.transform = `translate3d(${-nextIndex * step}px, 0, 0)`;

    window.requestAnimationFrame(() => updateProgress(section));
  };

  const setFromPointer = (section, clientX, animate = false) => {
    const metrics = getMetrics(section);
    if (!metrics) return;

    const { maxIndex, progress } = metrics;
    const rect = progress.getBoundingClientRect();
    const ratio = rect.width > 0 ? clamp((clientX - rect.left) / rect.width, 0, 1) : 0;

    setIndex(section, ratio * maxIndex, animate);
  };

  const wireProgress = (section) => {
    if (section.dataset.okgReviewsReady === 'true') {
      updateProgress(section);
      return;
    }

    const holder = section.querySelector('.reputon-reviews-holder');
    const viewport = section.querySelector('.swiper-horizontal');
    const wrapper = viewport?.firstElementChild;

    if (!holder || !viewport || !wrapper) return;

    let controls = section.querySelector(`.${controlsClass}`);
    let progress = section.querySelector(`.${progressClass}`);

    if (!controls || !progress) {
      const icon = '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

      controls = document.createElement('div');
      controls.className = controlsClass;
      controls.innerHTML = `
        <button type="button" class="okg-reviews-button okg-reviews-button--prev" aria-label="Previous reviews">${icon}</button>
        <div class="${progressClass}" aria-label="Scroll reviews" aria-valuemin="0" role="slider" tabindex="0">
          <span class="${progressBarClass}"></span>
        </div>
        <button type="button" class="okg-reviews-button okg-reviews-button--next" aria-label="Next reviews">${icon}</button>
      `;
      holder.insertAdjacentElement('afterend', controls);

      progress = controls.querySelector(`.${progressClass}`);
    } else if (!controls.contains(progress)) {
      controls.appendChild(progress);
    }

    const prevControl = controls.querySelector('.okg-reviews-button--prev');
    const nextControl = controls.querySelector('.okg-reviews-button--next');

    if (!progress) return;

    if (!progress.querySelector(`.${progressBarClass}`)) {
      progress.innerHTML = `<span class="${progressBarClass}"></span>`;
    }

    progress.setAttribute('aria-label', 'Scroll reviews');
    progress.setAttribute('aria-valuemin', '0');
    progress.setAttribute('role', 'slider');
    progress.setAttribute('tabindex', '0');

    let isDragging = false;

    prevControl?.addEventListener('click', () => {
      const metrics = getMetrics(section);
      if (!metrics) return;
      setIndex(section, metrics.activeIndex - 1);
    });

    nextControl?.addEventListener('click', () => {
      const metrics = getMetrics(section);
      if (!metrics) return;
      setIndex(section, metrics.activeIndex + 1);
    });

    progress.addEventListener('pointerdown', (event) => {
      isDragging = true;
      progress.classList.add('is-dragging');
      progress.setPointerCapture(event.pointerId);
      setFromPointer(section, event.clientX, false);
    });

    progress.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      setFromPointer(section, event.clientX, false);
    });

    progress.addEventListener('pointerup', (event) => {
      isDragging = false;
      progress.classList.remove('is-dragging');
      progress.releasePointerCapture(event.pointerId);
      setFromPointer(section, event.clientX, true);
    });

    progress.addEventListener('pointercancel', () => {
      isDragging = false;
      progress.classList.remove('is-dragging');
    });

    progress.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const metrics = getMetrics(section);
      if (!metrics) return;

      event.preventDefault();

      if (event.key === 'Home') {
        setIndex(section, 0);
      } else if (event.key === 'End') {
        setIndex(section, metrics.maxIndex);
      } else {
        setIndex(section, metrics.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    const observer = new MutationObserver(() => updateProgress(section));
    observer.observe(wrapper, { attributes: true, attributeFilter: ['style', 'class'] });

    window.addEventListener('resize', () => updateProgress(section), { passive: true });

    section.dataset.okgReviewsReady = 'true';
    updateProgress(section);
  };

  const init = () => {
    document.querySelectorAll(sectionSelector).forEach((section) => wireProgress(section));
  };

  const boot = () => {
    init();

    const observer = new MutationObserver(init);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
